/* Filing a diagram without leaving the app.
 *
 * The library is still the one place a file lives — it is where somebody who
 * has never opened Ledger will look, and inventing a second store would make
 * "where is the comms room photo" a question with two answers. What changes is
 * that you no longer have to go there, upload, find the RecordId column and
 * type the right value into it. That instruction was correct and nobody was
 * ever going to follow it.
 *
 * Drag or pick, and it stamps the metadata itself.
 */

import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOnline } from '../../hooks/useOnline';
import { useServices } from '../../services/context';
import { qk } from '../../query/keys';
import { Button } from '../ui';
import s from './screens.module.scss';

export function FileDrop({
  recordId,
  recordType,
  hint,
}: {
  /** What this file belongs to — "network-hull". */
  recordId: string;
  /** Which register it came from, for anybody browsing the library directly. */
  recordType: string;
  /** What is worth filing here. */
  hint: string;
}) {
  const svc = useServices();
  const qc = useQueryClient();
  const online = useOnline();
  const input = useRef<HTMLInputElement>(null);

  const [over, setOver] = useState(false);
  /* One at a time, with the name showing. Uploading four files and reporting
     only "uploading…" leaves you unable to tell which one is stuck. */
  const [current, setCurrent] = useState<string | null>(null);
  const [failed, setFailed] = useState<string[]>([]);

  const send = useMutation({

    meta: { quiet: true },
    mutationFn: async (chosen: File[]) => {
      const failures: string[] = [];
      setFailed([]);
      for (const file of chosen) {
        setCurrent(file.name);
        try {
          await svc.attachments.upload(recordId, recordType, file);
        } catch (err) {
          failures.push(`${file.name}: ${err instanceof Error ? err.message : 'failed'}`);
        }
      }
      setCurrent(null);
      setFailed(failures);
      return failures;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.attachments(recordId) });
    },
  });

  const take = (list: FileList | null) => {
    const chosen = [...(list ?? [])];
    if (chosen.length > 0) send.mutate(chosen);
  };

  return (
    <div className={s.padBox}>
      <div
        className={`${s.dropZone} ${over ? s.dropOver : ''}`}
        onDragOver={(e) => {
          /* Both, and both matter: without preventDefault the browser navigates
             to the file instead, replacing the app with a picture of a rack. */
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          if (online) take(e.dataTransfer.files);
        }}
      >
        <i className="ti ti-cloud-upload" aria-hidden />
        <div>
          <b>{send.isPending ? `Uploading ${current ?? ''}…` : 'Drop a file here'}</b>
          <span className={s.cellSub}>{hint}</span>
        </div>

        <Button
          icon={send.isPending ? 'ti-loader-2' : 'ti-file-plus'}
          spinning={send.isPending}
          disabled={!online || send.isPending}
          onClick={() => input.current?.click()}
        >
          Choose a file
        </Button>

        <input
          ref={input}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            take(e.target.files);
            /* Cleared, or picking the same file twice in a row fires no change
               event the second time and the upload silently does not happen. */
            e.target.value = '';
          }}
        />
      </div>

      {!online && <div className={s.hint}>Uploading needs a network.</div>}

      {failed.length > 0 && (
        <div className={`${s.ownedNote} ${s.saveError}`}>
          <i className="ti ti-alert-triangle" aria-hidden />
          <span>{failed.join(' · ')}</span>
        </div>
      )}
    </div>
  );
}
